import React, { useRef } from 'react'
import { useSelector } from 'react-redux'
import history from 'browserHistory'
import { Link } from 'react-router-dom'

import { selectors as ChannelsSelectors } from 'data/channels'
import { selectors as UserPreferencesSelectors } from 'data/userPreferences'
import { currentUserCanCreateNote } from 'data/rights/selectors'
import { BOOKMARKED_NOTES } from 'data/filters/types'
import { getChannel } from 'data/channels/selectors'
import { getChannelActivityStatus } from 'data/activityRegistry/selectors'
import { ACTIVE } from 'data/activityRegistry/record'

import RoutesHelper from 'helpers/RoutesHelper'
import ChannelIcon from 'components/Utils/ChannelIcon'
import { TooltipOnHover } from 'components/Utils/TooltipOnHover'
import { getChannelLabel } from 'data/channels/helpers'

import { RowLayoutHoverZone } from '../RowLayout'
import { EnhancedChannelControls } from './Controls'
import useChannelDrop from './useChannelDrop'

import useOnLongDragOver from '../../hooks/useOnLongDragOver'
import { ExpandableRowLayout } from '../ExpandableRowLayout'

export type ChannelItemProps = {
  id: string
  depth: number
  isActive: boolean
  isExpanded: boolean
  onToggleExpand: (
    id: string,
    options?: { collapseChannels?: boolean; checkRule42?: boolean }
  ) => void
  setLastClickedNoteId: (noteId: string) => void
  type: string
  name: string
}

export const ChannelItem = ({
  id,
  depth,
  isActive,
  isExpanded,
  onToggleExpand,
  setLastClickedNoteId,
  type,
  name,
}: ChannelItemProps) => {
  const layoutRef = useRef<HTMLAnchorElement>(null)
  const [isMorePopoverOpen, setMorePopoverIsOpen] = React.useState(false)

  const hasActivity = useSelector(
    state => getChannelActivityStatus(state, getChannel(state, id)) === ACTIVE
  )

  const canCreateNoteSelector = React.useCallback(
    state => {
      const channel = ChannelsSelectors.getChannel(state, id)
      return currentUserCanCreateNote(state, channel)
    },
    [id]
  )
  const canCreateNote = useSelector(canCreateNoteSelector)

  const { isDragOver, hoverZone } = useChannelDrop(id, canCreateNote, layoutRef)

  useOnLongDragOver({
    isDragOver,
    enable: !isExpanded,
    onLongDragOver() {
      onToggleExpand(id)
    },
  })

  const isFavorite = useSelector(function isFavoriteSelector(state) {
    return UserPreferencesSelectors.isChannelFavorite(state, id)
  })

  const onCreateNote = React.useCallback(
    createdNote => {
      setLastClickedNoteId(createdNote.id)
      const nextUrl = isFavorite
        ? RoutesHelper.noteFilterShow({
            filterId: BOOKMARKED_NOTES.get('id'),
            noteId: createdNote.id,
          })
        : RoutesHelper.noteChannelShow({
            channelId: id,
            noteId: createdNote.id,
          })

      history.push(nextUrl)
      if (!isExpanded) {
        onToggleExpand(id, { collapseChannels: true, checkRule42: true })
      }
    },
    [id, isFavorite, onToggleExpand, isExpanded, setLastClickedNoteId]
  )

  const title = getChannelLabel({ type, name })

  return (
    <ExpandableRowLayout
      renderComponent={props => (
        <TooltipOnHover
          id={`tooltip-sidebar-${id}`}
          tooltipContent={title}
          placement="right"
          delayShow={1000}
        >
          <Link {...props} to={RoutesHelper.channelShow({ channelId: id })} />
        </TooltipOnHover>
      )}
      id={id}
      onToggleExpand={onToggleExpand}
      expandOptions={{ collapseChannels: true, checkRule42: true }}
      isExpanded={isExpanded}
      className="child-effects-on-hover"
      depth={depth}
      label={getChannelLabel({ type, name })}
      isActive={isActive}
      isDragOver={isDragOver}
      hasActivity={hasActivity}
      hoverZone={hoverZone as RowLayoutHoverZone}
      ref={layoutRef}
      icon={<ChannelIcon type={type} useNewIcons isExpanded={isExpanded} />}
      rightPart={
        <EnhancedChannelControls
          className={
            isMorePopoverOpen ? '' : 'display-none flexbox-child-effect'
          }
          channelId={id}
          canCreateNote={canCreateNote}
          onMorePopoverIsOpenChange={setMorePopoverIsOpen}
          onCreateNote={onCreateNote}
        />
      }
    />
  )
}
